import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiSearch, FiRefreshCw, FiDownload, FiPackage, FiAlertTriangle, FiCheckCircle, FiClock, FiChevronDown, FiChevronUp, FiPlay, FiEye, FiInfo,
} from 'react-icons/fi';
import css from './ConsultaNfs.module.css';
import cx from './ConsultaNfe.module.css';
import {
  listarClientesNfe, listarNotasNfe, listarExecucoesNfe, sincronizarClienteNfe, ativarBuscaNfe, executarCapturaNfeTodos, manifestarNfe, baixarNfe, urlZipNfe,
} from '../../services/nfeCaptura';
import { ehPortal } from '../../utils/portal';

/* ── Consulta NF Produtos ──
   NF-e (modelo 55) emitidas e recebidas de cada cliente, pela Distribuição de
   DF-e da SEFAZ (Ambiente Nacional) com o certificado A1 do cliente. Nota
   recebida chega primeiro como RESUMO; o XML completo só vem depois da
   Ciência da Operação. A SEFAZ só deixa consultar de novo 1h depois de
   esgotar a fila de cada CNPJ. */

const STATUS = {
  nunca: ['Ainda não sincronizado', css.stNeutro], rodando: ['Sincronizando...', css.stRodando], ok: ['Em dia', css.stOk],
  erro: ['Erro na última consulta', css.stErro], limite: ['Consumo indevido (SEFAZ 656): nova consulta em 1h', css.stAlerta],
  sem_certificado: ['Sem certificado', css.stErro], certificado_vencido: ['Certificado vencido', css.stErro], senha_invalida: ['Senha do certificado inválida', css.stErro],
  sem_uf: ['Cadastro sem UF/município', css.stErro],
};
const PENDENTES = ['erro', 'limite', 'sem_certificado', 'certificado_vencido', 'senha_invalida', 'sem_uf'];
const MANIF = { ciencia: 'Ciência', confirmada: 'Confirmada', desconhecida: 'Desconhecida', nao_realizada: 'Não realizada' };
const moeda = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBr = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—');
const quando = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const hora = (iso) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
const doc = (d) => {
  const x = String(d || '').replace(/\D/g, '');
  if (x.length === 14) return x.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (x.length === 11) return x.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return d || '';
};
const mesAtual = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

function ConsultaNfe() {
  const [dados, setDados] = useState({ items: [], ambiente: 2 });
  const [verPendencias, setVerPendencias] = useState(false);
  const [busca, setBusca] = useState('');
  const [sel, setSel] = useState(null);
  const [modoData, setModoData] = useState('mes');
  const [filtro, setFiltro] = useState({ competencia: mesAtual(), inicio: '', fim: '', tipo: '', situacao: 'ativa', completo: '' });
  const [notas, setNotas] = useState({ items: [], totais: { quantidade: 0, valor: 0, icms: 0, resumos: 0 } });
  const [carregandoNotas, setCarregandoNotas] = useState(false);
  const [manifestando, setManifestando] = useState(false);
  const [execucoes, setExecucoes] = useState([]);
  const [verLog, setVerLog] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const sincronizandoAntes = useRef(false);

  const carregarClientes = useCallback(() => listarClientesNfe().then(setDados).catch(e => setErro(e.message)), []);
  useEffect(() => { carregarClientes(); }, [carregarClientes]);

  const cliente = dados.items.find(c => c.id === sel) || null;
  const filtroApi = sel ? {
    cliente_id: sel, tipo: filtro.tipo, situacao: filtro.situacao, completo: filtro.completo,
    ...(modoData === 'mes' ? { competencia: filtro.competencia } : { inicio: filtro.inicio, fim: filtro.fim }),
  } : null;
  const chaveFiltro = JSON.stringify(filtroApi);

  const carregarNotas = useCallback(() => {
    if (!filtroApi) return Promise.resolve();
    setCarregandoNotas(true); setErro('');
    return listarNotasNfe(filtroApi).then(setNotas).catch(e => setErro(e.message)).finally(() => setCarregandoNotas(false));
  }, [chaveFiltro]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregarNotas(); }, [carregarNotas]);
  useEffect(() => { if (sel) listarExecucoesNfe(sel).then(r => setExecucoes(r.items || [])).catch(() => {}); }, [sel, notas]);

  const sincronizando = !!cliente?.sincronizando || dados.jobRodando;
  useEffect(() => {
    if (!sincronizando) {
      if (sincronizandoAntes.current) { sincronizandoAntes.current = false; carregarNotas(); }
      return undefined;
    }
    sincronizandoAntes.current = true;
    const t = setInterval(carregarClientes, 4000);
    return () => clearInterval(t);
  }, [sincronizando, carregarClientes, carregarNotas]);

  const bloqueadoAte = cliente?.proxima_consulta && new Date(cliente.proxima_consulta) > new Date() ? cliente.proxima_consulta : null;

  async function sincronizar() {
    setErro(''); setMsg('');
    try { await sincronizarClienteNfe(sel); setMsg('Sincronização iniciada. A primeira carga pode levar alguns minutos.'); await carregarClientes(); }
    catch (e) { setErro(e.message); }
  }
  async function alternarBusca() {
    const ligar = !cliente.busca_ativa;
    if (!window.confirm(ligar
      ? `Ativar a busca de NF-e deste cliente no nosso sistema?\n\nSó ative se ele NÃO for mais buscado pelo SIEG (ou outro sistema): a SEFAZ tem uma fila só por CNPJ e os dois sistemas perdem documentos um para o outro.`
      : `Desativar a busca de NF-e deste cliente? Ele sai do job da madrugada e o botão de sincronizar fica bloqueado.`)) return;
    setErro(''); setMsg('');
    try { await ativarBuscaNfe(cliente.id, ligar); setMsg(ligar ? 'Busca ativada. Use "Sincronizar agora" ou aguarde o job da madrugada.' : 'Busca desativada.'); await carregarClientes(); }
    catch (e) { setErro(e.message); }
  }
  async function capturarTodos() {
    if (!window.confirm('Capturar agora as NF-e de todos os clientes com certificado? Roda em segundo plano, um cliente por vez; quem consultou há menos de 1h é pulado (regra da SEFAZ).')) return;
    setErro(''); setMsg('');
    try { await executarCapturaNfeTodos(); setMsg('Captura de todos iniciada em segundo plano.'); carregarClientes(); } catch (e) { setErro(e.message); }
  }
  async function darCiencia(corpo, qtd) {
    if (!window.confirm(`Registrar a Ciência da Operação em ${qtd} nota(s) recebida(s)?\n\nA ciência libera o XML completo na próxima consulta, mas NÃO confirma o recebimento. A manifestação final (confirmação, desconhecimento ou operação não realizada) precisa ser feita em até 180 dias.`)) return;
    setErro(''); setMsg(''); setManifestando(true);
    try {
      const r = await manifestarNfe(sel, { tipo: 'ciencia', ...corpo });
      const falha = r.resultados.find(x => !x.ok);
      setMsg(`Ciência registrada em ${r.ok} nota(s).${r.falhas ? ` ${r.falhas} falharam${falha ? ` (${falha.mensagem})` : ''}.` : ''} O XML completo chega na próxima sincronização.`);
      carregarNotas(); carregarClientes();
    } catch (e) { setErro(e.message); } finally { setManifestando(false); }
  }
  async function baixar(caminho, nome) {
    setErro('');
    try { await baixarNfe(caminho, nome); } catch (e) { setErro(e.message); }
  }

  /* usuário de cliente: a lista é só ele -- já entra selecionado e sem a
     barra lateral de clientes, com a tela inteira pras notas */
  const portal = ehPortal();
  useEffect(() => {
    if (portal && !sel && dados.items[0]) setSel(dados.items[0].id);
  }, [portal, sel, dados.items]);

  const q = busca.trim().toLowerCase();
  const lista = dados.items.filter(c => !q || `${c.codigo} ${c.nome}`.toLowerCase().includes(q));
  const pendencias = dados.items.filter(c => PENDENTES.includes(c.status));
  const [stTexto, stCls] = STATUS[cliente?.sincronizando ? 'rodando' : (cliente?.status || 'nunca')] || STATUS.nunca;
  const resumosSemCiencia = notas.items.filter(n => n.tipo === 'recebida' && !n.completo && !n.manifestacao && n.situacao === 'autorizada').length;

  return (
    <div className={css.page}>
      <div className={css.header}>
        <div className={css.headerIcone}><FiPackage size={20} /></div>
        <div>
          <h1 className={css.titulo}>Consulta NF Produtos</h1>
          <p className={css.sub}>NF-e (modelo 55) emitidas e recebidas, pela Distribuição de DF-e da SEFAZ com o certificado A1 de cada cliente</p>
        </div>
        <span className={dados.ambiente === 1 ? css.ambProd : css.ambHom}>{dados.ambiente === 1 ? 'Produção' : 'Homologação'}</span>
        {!ehPortal() && (
        <button type="button" className={css.btnSec} onClick={capturarTodos} disabled={dados.jobRodando}>
          <FiPlay size={13} /> {dados.jobRodando ? 'Capturando todos...' : 'Capturar todos agora'}
        </button>
        )}
      </div>

      <div className={cx.aviso}>
        <FiInfo size={14} />
        <span>Busca as notas de <strong>entrada</strong> (fornecedores contra o cliente). Cada busca já registra a <strong>Ciência da Operação</strong> nos resumos, e o XML completo chega na sincronização seguinte: a SEFAZ só libera nova consulta de cada CNPJ <strong>1 hora</strong> depois (o job da madrugada faz a 2ª passada sozinho, a partir das 03h). Notas de saída do cliente não vêm por este serviço.</span>
      </div>

      {pendencias.length > 0 && (
        <div className={css.pendencias}>
          <button type="button" onClick={() => setVerPendencias(v => !v)}>
            <FiAlertTriangle size={14} /> {pendencias.length} cliente(s) com pendência na captura (certificado, cadastro ou erro)
            {verPendencias ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
          </button>
          {verPendencias && (
            <ul>
              {pendencias.map(p => (
                <li key={p.id}><strong>{p.codigo} - {p.nome}</strong>: {(STATUS[p.status] || [p.status])[0]}{p.ultimo_erro ? ` — ${p.ultimo_erro}` : ''}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {erro && <p className={css.erro}>{erro}</p>}
      {msg && <p className={css.ok}>{msg}</p>}

      <div className={css.corpo} style={portal ? { gridTemplateColumns: 'minmax(0, 1fr)' } : undefined}>
        {!portal && (
        <aside className={css.lista}>
          <div className={css.busca}><FiSearch size={13} /><input placeholder="Buscar cliente..." value={busca} onChange={e => setBusca(e.target.value)} /></div>
          <p className={css.listaInfo}>{dados.items.length} cliente(s) com certificado</p>
          {lista.map(c => {
            const [, cls] = STATUS[c.sincronizando ? 'rodando' : (c.status || 'nunca')] || STATUS.nunca;
            return (
              <button key={c.id} type="button" className={`${css.itemCliente} ${sel === c.id ? css.itemAtivo : ''}`} onClick={() => setSel(c.id)}>
                <span className={`${css.ponto} ${cls}`} />
                <span className={css.itemTexto}>
                  <strong>{c.codigo} - {c.nome}</strong>
                  <small>{!c.busca_ativa && <span className={cx.tagDesativada}>DESATIVADA</span>}{c.notas} nota(s){c.resumos ? ` · ${c.resumos} resumo(s)` : ''} · {c.ultima_sincronizacao ? quando(c.ultima_sincronizacao) : 'nunca sincronizado'}</small>
                </span>
              </button>
            );
          })}
          {!lista.length && <p className={css.vazio}>Nenhum cliente com certificado A1 cadastrado no menu Certificados.</p>}
        </aside>
        )}

        <section className={css.detalhe}>
          {!cliente ? (
            <div className={css.placeholder}><FiPackage size={34} /><p>Escolha um cliente para ver as notas.</p></div>
          ) : (
            <>
              <div className={css.cliTopo}>
                <div>
                  <h2>{cliente.codigo} - {cliente.nome}</h2>
                  <p>{doc(cliente.cnpj)} · certificado vence {cliente.certificado_vence ? cliente.certificado_vence.split('-').reverse().join('/') : '—'} · NSU {cliente.ultimo_nsu ?? 0}{cliente.max_nsu ? ` de ${cliente.max_nsu}` : ''}</p>
                  {!ehPortal() && (
                  <label className={cx.chaveBusca} title="A SEFAZ tem uma fila só por CNPJ: ative só quando o SIEG (ou outro sistema) parar de buscar este cliente">
                    <input type="checkbox" checked={!!cliente.busca_ativa} onChange={alternarBusca} />
                    <span className={cx.chaveTrilho}><span /></span>
                    {cliente.busca_ativa ? 'Busca ativa no nosso sistema' : 'Busca desativada (ainda no SIEG)'}
                  </label>
                  )}
                  <span className={`${css.status} ${stCls}`}>{stTexto}{cliente.status !== 'ok' && cliente.ultimo_erro ? `: ${cliente.ultimo_erro}` : ''}</span>
                </div>
                <div className={cx.acoesTopo}>
                  {!ehPortal() && (
                  <button type="button" className={css.btnPri} onClick={sincronizar} disabled={sincronizando || !!bloqueadoAte || !cliente.busca_ativa}>
                    <FiRefreshCw size={13} className={sincronizando ? css.girando : ''} /> {sincronizando ? 'Sincronizando...' : 'Sincronizar agora'}
                  </button>
                  )}
                  {bloqueadoAte && !sincronizando && <small className={cx.bloqueio}><FiClock size={11} /> SEFAZ libera nova consulta às {hora(bloqueadoAte)}</small>}
                </div>
              </div>

              <div className={css.filtros}>
                <div className={css.segmento}>
                  <button type="button" className={modoData === 'mes' ? css.segAtivo : ''} onClick={() => setModoData('mes')}>Mês</button>
                  <button type="button" className={modoData === 'periodo' ? css.segAtivo : ''} onClick={() => setModoData('periodo')}>Período</button>
                </div>
                {modoData === 'mes' ? (
                  <input type="month" className={css.input} value={filtro.competencia} onChange={e => setFiltro(f => ({ ...f, competencia: e.target.value }))} title="Mês da emissão" />
                ) : (
                  <>
                    <input type="date" className={css.input} value={filtro.inicio} onChange={e => setFiltro(f => ({ ...f, inicio: e.target.value }))} title="Data inicial (emissão)" />
                    <span className={css.ate}>até</span>
                    <input type="date" className={css.input} value={filtro.fim} onChange={e => setFiltro(f => ({ ...f, fim: e.target.value }))} title="Data final (emissão)" />
                  </>
                )}
                <select className={css.input} value={filtro.tipo} onChange={e => setFiltro(f => ({ ...f, tipo: e.target.value }))}>
                  <option value="">Emitidas e recebidas</option>
                  <option value="emitida">Emitidas</option>
                  <option value="recebida">Recebidas</option>
                </select>
                <select className={css.input} value={filtro.situacao} onChange={e => setFiltro(f => ({ ...f, situacao: e.target.value }))}>
                  <option value="ativa">Autorizadas</option>
                  <option value="cancelada">Canceladas/denegadas</option>
                  <option value="todas">Todas</option>
                </select>
                <select className={css.input} value={filtro.completo} onChange={e => setFiltro(f => ({ ...f, completo: e.target.value }))}>
                  <option value="">XML completo e resumos</option>
                  <option value="1">Só XML completo</option>
                  <option value="0">Só resumos</option>
                </select>
                <span className={css.espaco} />
                {resumosSemCiencia > 0 && (
                  <button type="button" className={css.btnSec} disabled={manifestando} onClick={() => darCiencia({ todos_resumos: true }, cliente.resumos || resumosSemCiencia)}>
                    <FiEye size={13} /> {manifestando ? 'Registrando...' : 'Dar ciência nos resumos'}
                  </button>
                )}
                <button type="button" className={css.btnPri} disabled={!notas.items.some(n => n.completo)} title="Só notas com XML completo (resumo não importa no Domínio)"
                  onClick={() => baixar(urlZipNfe(filtroApi), 'nfe.zip')}>
                  <FiDownload size={13} /> Baixar ZIP
                </button>
              </div>

              <div className={cx.totais}>
                <div><span>Notas</span><strong>{notas.totais.quantidade}</strong></div>
                <div><span>Valor total</span><strong>{moeda(notas.totais.valor)}</strong></div>
                <div><span>ICMS</span><strong>{moeda(notas.totais.icms)}</strong></div>
                <div><span>Só resumo</span><strong>{notas.totais.resumos}</strong></div>
              </div>

              <div className={css.tabelaWrap}>
                <table className={css.tabela}>
                  <thead>
                    <tr><th>Emissão</th><th>Nº / Série</th><th>Tipo</th><th>Emitente / Destinatário</th><th>Natureza</th><th className={css.num}>Valor</th><th className={css.num}>ICMS</th><th>Situação</th><th>XML</th><th /></tr>
                  </thead>
                  <tbody>
                    {carregandoNotas ? <tr><td colSpan={10} className={css.vazio}>Carregando...</td></tr>
                      : !notas.items.length ? <tr><td colSpan={10} className={css.vazio}>Nenhuma nota nesse filtro.</td></tr>
                        : notas.items.map(n => {
                          const outro = n.tipo === 'emitida' ? { rot: 'Destinatário', nome: n.destinatario_nome, doc: n.destinatario_doc } : { rot: 'Emitente', nome: n.emitente_nome, doc: n.emitente_doc };
                          const podeCiencia = n.tipo === 'recebida' && !n.completo && !n.manifestacao && n.situacao === 'autorizada';
                          return (
                            <tr key={n.id} className={n.situacao !== 'autorizada' ? css.linhaCancelada : ''}>
                              <td>{dataBr(n.data_emissao)}</td>
                              <td>{n.numero ? `${n.numero}${n.serie ? ` / ${n.serie}` : ''}` : <span className={cx.chave} title={n.chave_acesso}>…{n.chave_acesso.slice(-12)}</span>}</td>
                              <td><span className={n.tipo === 'emitida' ? css.tEmitida : css.tRecebida}>{n.tipo === 'emitida' ? 'Emitida' : 'Recebida'}</span></td>
                              <td className={css.contra}><small>{outro.rot}</small>{outro.nome || '—'}<em>{doc(outro.doc)}</em></td>
                              <td className={cx.natureza}>{n.natureza_operacao || '—'}</td>
                              <td className={css.num}>{moeda(n.valor_total)}</td>
                              <td className={css.num}>{n.completo ? moeda(n.valor_icms) : '—'}</td>
                              <td>
                                {n.situacao === 'autorizada' ? <span className={css.sAtiva}><FiCheckCircle size={11} /> Autorizada</span>
                                  : <span className={css.sCancelada}>{n.situacao === 'denegada' ? 'Denegada' : 'Cancelada'}</span>}
                                {n.manifestacao && <em className={cx.manif}>{MANIF[n.manifestacao]}{n.manifestacao_em ? ` em ${dataBr(n.manifestacao_em)}` : ''}</em>}
                              </td>
                              <td>{n.completo ? <span className={cx.completo}>Completo</span> : <span className={cx.resumo}>Resumo</span>}</td>
                              <td className={css.acoes}>
                                {podeCiencia && <button type="button" title="Dar Ciência da Operação (libera o XML completo)" disabled={manifestando} onClick={() => darCiencia({ chaves: [n.chave_acesso] }, 1)}>Ciência</button>}
                                <button type="button" title={n.completo ? 'Baixar XML' : 'Baixar o resumo (resNFe)'} onClick={() => baixar(`/notas/${n.id}/xml`, `${n.chave_acesso}.xml`)}>XML</button>
                              </td>
                            </tr>
                          );
                        })}
                  </tbody>
                </table>
              </div>

              <div className={css.log}>
                <button type="button" onClick={() => setVerLog(v => !v)}><FiClock size={13} /> Execuções deste cliente {verLog ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}</button>
                {verLog && (
                  <table className={css.tabela}>
                    <thead><tr><th>Início</th><th>Origem</th><th>Resumos novos</th><th>Completas</th><th>Ciências</th><th>Eventos</th><th>Erros</th><th>Duração</th><th>Mensagem</th></tr></thead>
                    <tbody>
                      {execucoes.map(e => (
                        <tr key={e.id}>
                          <td>{quando(e.inicio)}</td><td>{({ ciencia: 'ciência manual', complemento: 'agendado (2ª passada)' })[e.origem] || e.origem}</td><td>{e.notas_novas}</td><td>{e.completas}</td><td>{e.ciencias ?? 0}</td><td>{e.eventos}</td><td>{e.erros}</td>
                          <td>{e.duracao_ms != null ? `${Math.round(e.duracao_ms / 1000)}s` : (e.fim ? '—' : '…')}</td><td className={css.msgLog}>{e.mensagem || '—'}</td>
                        </tr>
                      ))}
                      {!execucoes.length && <tr><td colSpan={9} className={css.vazio}>Nenhuma execução ainda.</td></tr>}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default ConsultaNfe;
